import * as React from 'react';

import { toZoned } from '@internationalized/date';
import {
  enumActionType,
  enumServiceStatus,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  $resetServiceForm,
  $serviceForm,
  $updateServiceForm,
} from '@rematter/modules/transportation/state/serviceForm.localState';
import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  Toggle,
  ToggleButtonGroup,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { useServiceCreateMutation } from '../../hooks/mutations/useServiceCreateMutation';
import { useUpdateServiceMutation } from '../../hooks/mutations/useUpdateServiceMutation';

import { ServiceForm } from './ServiceForm';
import { ServiceStatusActions } from './ServiceStatusActions';

import { ServiceFormState } from '~/modules/transportation/types';
import { $organization } from '~/shared/providers/auth/identity.localState';
import { $localization } from '~/shared/state/preferences.state';

type ServiceDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  service?: ServiceFormState & { id: string; documentNumber?: string };
};

export const ServiceDialog = ({
  isOpen,
  onClose,
  onSuccess,
  service,
}: ServiceDialogProps) => {
  const t = useTranslations();
  const [isViewMode, setIsViewMode] = React.useState(false);
  const [formMode, setFormMode] = React.useState<'simple' | 'advanced'>(
    'simple',
  );
  const localization = useAtomValue($localization);
  const form = useAtomValue($serviceForm);
  const organization = useAtomValue($organization);
  const updateForm = useSetAtom($updateServiceForm);
  const resetForm = useSetAtom($resetServiceForm);
  const toast = useToast();

  const { mutateAsync: createService, isPending: isCreating } =
    useServiceCreateMutation();
  const { mutateAsync: updateService, isPending: isUpdating } =
    useUpdateServiceMutation();

  const isDriverStarted =
    !!service &&
    service.status !== enumServiceStatus.Draft &&
    service.status !== enumServiceStatus.Planned;

  React.useEffect(() => {
    if (service) {
      updateForm(service);
    } else if (organization) {
      updateForm({ haulerId: organization.id });
    }
    setIsViewMode(isDriverStarted);
  }, [service, updateForm, organization, isDriverStarted]);

  const handleClose = () => {
    onClose();
    setIsViewMode(false);
    setFormMode('simple');
    resetForm();
  };

  const handleSave = async () => {
    try {
      const scheduledWindowStart = toZoned(
        form.scheduledWindowStart,
        localization.timezone,
      );
      const scheduledWindowEnd = toZoned(
        form.scheduledWindowEnd,
        localization.timezone,
      );
      const externalHaulerId =
        form.haulerId && form.haulerId !== organization?.id
          ? form.haulerId
          : undefined;

      const actions = form.assetQuantities
        ?.filter(
          (
            aq,
          ): aq is typeof aq & {
            assetType: NonNullable<typeof aq.assetType> & { id: string };
          } => !!aq.assetType?.id,
        )
        .map((aq) => ({
          actionType: enumActionType.Pickup,
          assetTypeId: aq.assetType.id,
          quantity: aq.quantity,
          assetGroup: aq.assetGroup,
        }));

      const manualAddress =
        !form.facilityId && !form.accountAddressId && form.address
          ? form.address
          : undefined;

      const formData = {
        ...form,
        scheduledWindowStart,
        scheduledWindowEnd,
        facilityGroupId: form.facilityGroupId ?? undefined,
        facilityId: form.facilityId ?? undefined,
        accountId: form.accountId ?? undefined,
        accountAddressId: form.accountAddressId ?? undefined,
        contactId: form.contactId ?? undefined,
        driverId: form.driverId ?? undefined,
        haulerId: externalHaulerId,
        truckId: form.truckId ?? undefined,
        trailerId: form.trailerId ?? undefined,
        routeId: form.routeId ?? undefined,
        actions,
        address: manualAddress
          ? {
              streetAddress: manualAddress.streetAddress,
              city: manualAddress.city,
              state: manualAddress.state,
              postalCode: manualAddress.postalCode,
              country: manualAddress.country,
              addressType: manualAddress.addressType,
              isMailingAddress: false,
            }
          : undefined,
      };

      if (service) {
        await updateService({
          id: service.id,
          ...formData,
          serviceTypeId: form.typeId ?? '',
        });
        toast.success(t.servicesView.form.messages.updateSuccess);
        setIsViewMode(true);
      } else {
        await createService({
          ...formData,
          serviceTypeId: form.typeId ?? '',
        });
        toast.success(t.servicesView.form.messages.createSuccess);
      }
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.servicesView.form.messages.error,
      );
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      isDismissable={false}
      className="@ServiceDialog"
    >
      <DialogContent
        position="right"
        className="min-w-[600px]"
        dialogClassName="grid-rows-[auto_1fr_auto]"
        aria-label={
          service?.documentNumber
            ? t.servicesView.form.title.service(service.documentNumber)
            : service
              ? t.servicesView.form.title.view
              : t.servicesView.form.title.create
        }
      >
        <DialogHeader slot="title">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-xl font-semibold">
                {service?.documentNumber
                  ? t.servicesView.form.title.service(service.documentNumber)
                  : service
                    ? t.servicesView.form.title.view
                    : t.servicesView.form.title.create}
              </div>
              {service && isViewMode && (
                <Button
                  onPress={() => setIsViewMode(false)}
                  variant="link"
                  className="h-auto w-fit p-0 text-sm text-primary-darker hover:text-primary/70"
                >
                  {t.servicesView.form.buttons.edit}
                </Button>
              )}
            </div>
            <ToggleButtonGroup
              selectionMode="single"
              selectedKeys={new Set([formMode])}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as 'simple' | 'advanced';
                if (value) setFormMode(value);
              }}
            >
              <Toggle id="simple">{t.servicesView.form.modes.simple}</Toggle>
              <Toggle id="advanced">
                {t.servicesView.form.modes.advanced}
              </Toggle>
            </ToggleButtonGroup>
          </div>
        </DialogHeader>

        <DialogMain>
          <ServiceForm isViewMode={isViewMode} mode={formMode} />
        </DialogMain>

        <DialogFooter>
          {isViewMode && service ? (
            <div className="flex w-full items-center justify-end gap-2">
              <ServiceStatusActions
                service={service}
                onSuccess={onSuccess}
                onClose={handleClose}
              />
            </div>
          ) : (
            <>
              <Button
                color="failure"
                variant="outline"
                isPending={isLoading}
                onPress={handleClose}
                isLoading={isLoading}
              >
                {t.servicesView.form.buttons.cancel}
              </Button>
              <Button
                color="primary"
                variant="solid"
                isPending={isLoading}
                onPress={handleSave}
                isLoading={isLoading}
              >
                {service
                  ? t.servicesView.form.buttons.save
                  : t.servicesView.form.buttons.create}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
